/**
 * The FinOps tab (Operate › FinOps, design board "FinOps"): what this project costs, in Flow, for AI and in production.
 * Nothing here is invented, and every estimate says so.
 *
 * Every figure comes from vfs-service's FinOps model (`GET /api/v1/projects/:id/finops`, services/FinOpsService.js),
 * the same one the vfs_finops AI tool answers from, with the prices and assumptions the Flow admin sets (Flow admin ›
 * Settings › FinOps). This panel only sends the person's choices (runtime, region, the What if sliders, hours a day)
 * and formats the answer (utils/finopsFormat.js). The answer is asked again when a choice changes (after a short pause,
 * the last figures staying meanwhile) and every 15 seconds, for what the services use now.
 *
 * - Running in Flow: what each service uses NOW (measured) and the plan's credit rates (billing, real), so credits an
 *   hour (an estimate from now) and "if it runs N h a day" a month (an estimate). Flow does not meter usage per project
 *   yet, and the price of a credit is the admin's: the section says both. Your sandbox's credits this month (all
 *   projects, metered: CreditsService getMyCredits) are shown as such.
 * - AI for this project: Flow has no per-project AI cost yet: "AI Foundry: coming" and what will appear; the person's
 *   own AI tokens this month (all projects, metered, only with Chat on) where the credits answer has them.
 * - In production and What if: the model's estimate from the services' measured size (or a stated default), the
 *   project's datastores, the runtime and region chosen and the sliders; the sources and assumptions listed under it.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { getMyCredits } from '../../services/CreditsService';
import { getFinOps } from '../../services/FinOpsService';
import { REQUEST_STEPS, cores, credits, gb, money, requestStepIndex, shortCount } from '../../utils/finopsFormat';
import './FinOps.css';

const REFRESH_MS = 15_000;
const DEBOUNCE_MS = 150;

const Estimate = ({ children = 'estimate' }) => <span className="fo-tag is-estimate">{children}</span>;
Estimate.propTypes = { children: PropTypes.node };
const Measured = ({ children = 'measured' }) => <span className="fo-tag is-measured">{children}</span>;
Measured.propTypes = { children: PropTypes.node };

function flowColumns(hoursPerDay) {
  return [
    { key: 'name', label: 'Service', className: 'fo-mono', value: (r) => r.name },
    { key: 'state', label: 'State', width: 120, value: (r) => (r.running ? 'running' : 'stopped'), render: (r) => (r.running ? <Measured>running</Measured> : <span className="fo-muted">stopped</span>) },
    { key: 'cpu', label: 'CPU now', width: 110, value: (r) => r.vcpu, render: (r) => cores(r.vcpu) },
    { key: 'memory', label: 'Memory now', width: 120, value: (r) => r.memoryGb, render: (r) => gb(r.memoryGb) },
    { key: 'perHour', label: 'Credits/h', width: 110, value: (r) => r.creditsPerHour, render: (r) => credits(r.creditsPerHour) },
    { key: 'perMonth', label: `Credits/month at ${hoursPerDay} h a day`, width: 200, value: (r) => r.creditsPerMonth, render: (r) => credits(r.creditsPerMonth) },
  ];
}

function prodColumns(symbol) {
  return [
    {
      key: 'name',
      label: 'Component',
      value: (c) => c.name,
      title: (c) => (c.category === 'compute' ? `${c.name} · ${c.observed ? 'sized from Flow' : 'default size (not running now)'}` : c.name),
      render: (c) => (
        <>
          <span className={c.category === 'compute' ? 'fo-mono' : ''}>{c.name}</span>
          {c.category === 'compute' && <span className="fo-muted fo-small"> · {c.observed ? 'sized from Flow' : 'default size (not running now)'}</span>}
        </>
      ),
    },
    { key: 'replicas', label: 'Replicas', width: 100, value: (c) => c.replicas, render: (c) => c.replicas ?? '' },
    { key: 'vcpu', label: 'vCPU', width: 90, value: (c) => c.vcpu, render: (c) => c.vcpu ?? '' },
    { key: 'memory', label: 'Memory', width: 110, value: (c) => c.memoryGb, render: (c) => (c.memoryGb === null ? '' : gb(c.memoryGb)) },
    { key: 'monthly', label: `${symbol}/month`, width: 120, value: (c) => c.monthly, render: (c) => money(c.monthly, { symbol }) },
  ];
}

function scenarioColumns(symbol) {
  return [
    { key: 'label', label: 'Scenario', rowHeader: true, value: (r) => r.label },
    { key: 'requests', label: 'Requests/day', width: 130, value: (r) => r.traffic.requestsPerDay, render: (r) => shortCount(r.traffic.requestsPerDay) },
    { key: 'replicas', label: 'Replicas', width: 110, value: (r) => r.traffic.maxReplicas, render: (r) => (r.traffic.minReplicas === r.traffic.maxReplicas ? r.traffic.minReplicas : `${r.traffic.minReplicas} to ${r.traffic.maxReplicas}`) },
    { key: 'total', label: `${symbol}/month`, width: 120, value: (r) => r.estimate.total, render: (r) => money(r.estimate.total, { symbol }) },
    { key: 'perThousand', label: `${symbol} per 1k requests`, width: 160, value: (r) => r.estimate.perThousand, render: (r) => money(r.estimate.perThousand, { cents: true, symbol }) },
  ];
}

function Slider({ id, label, value, min, max, step = 1, onChange, valueText, hint = null }) {
  return (
    <div className="fo-slider">
      <div className="fo-slider-head">
        <label htmlFor={id}>{label}</label>
        <output htmlFor={id}>{valueText}</output>
      </div>
      <input aria-valuetext={valueText} id={id} max={max} min={min} onChange={(e) => onChange(Number(e.target.value))} step={step} type="range" value={value} />
      {hint && <p className="fo-muted fo-small">{hint}</p>}
    </div>
  );
}
Slider.propTypes = {
  hint: PropTypes.node, id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, max: PropTypes.number.isRequired, min: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired, step: PropTypes.number, value: PropTypes.number.isRequired, valueText: PropTypes.string.isRequired,
};

/** The stacked bars: one per row, its categories stacked bottom-up, each segment with its own tooltip (title). */
function StackedChart({ rows, categories, symbol }) {
  const max = Math.max(...rows.map((r) => r.estimate.total), 1);
  const H = 160;
  const barW = 44;
  const gap = 36;
  const width = rows.length * (barW + gap) + gap;
  // Room above the tallest bar for its total (the label sits 6px over the bar's top).
  const headroom = 24;
  return (
    <figure className="fo-chart" data-testid="finops-chart">
      <svg aria-label="Monthly estimate by scenario, stacked by category" role="img" viewBox={`0 ${-headroom} ${width} ${H + 40 + headroom}`}>
        <line className="fo-chart-base" x1={0} x2={width} y1={H + 4} y2={H + 4} />
        {rows.map((row, i) => {
          const x = gap + i * (barW + gap);
          let y = H + 4;
          const parts = categories.map((c) => ({ ...c, value: row.estimate.byCategory[c.id] || 0 })).filter((p) => p.value > 0);
          return (
            <g data-testid={`finops-bar-${row.id}`} key={row.id}>
              {parts.map((p, j) => {
                const h = Math.max(1, (p.value / max) * H);
                y -= h;
                const top = j === parts.length - 1;
                // 2px of surface between segments; the top segment's end rounded.
                return (
                  <rect className={`fo-seg is-${p.id}`} height={Math.max(0.5, h - 2)} key={p.id} rx={top ? 4 : 0} width={barW} x={x} y={y + (top ? 0 : 2)}>
                    <title>{`${row.label}: ${p.label} ${money(p.value, { symbol })} a month (estimate)`}</title>
                  </rect>
                );
              })}
              <text className="fo-chart-value" textAnchor="middle" x={x + barW / 2} y={y - 6}>{money(row.estimate.total, { symbol })}</text>
              <text className="fo-chart-label" textAnchor="middle" x={x + barW / 2} y={H + 22}>{row.label}</text>
            </g>
          );
        })}
      </svg>
      <figcaption className="fo-legend">
        {categories.map((c) => <span key={c.id}><i aria-hidden className={`fo-swatch is-${c.id}`} />{c.label}</span>)}
      </figcaption>
    </figure>
  );
}
StackedChart.propTypes = { categories: PropTypes.array.isRequired, rows: PropTypes.array.isRequired, symbol: PropTypes.string.isRequired };

/** The choices sent to the model, from its answer's `params` (the admin's defaults the first time). */
const choicesOf = (params) => ({ runtime: params.runtime, region: params.region, hoursPerDay: params.hoursPerDay, ...params.traffic });

export default function FinOpsPanel({ projectId }) {
  const [credit, setCredit] = useState({ state: 'loading', data: null });
  const [answer, setAnswer] = useState({ state: 'loading', data: null });
  // null until the first answer: the admin's defaults are the model's, not this page's.
  const [choices, setChoices] = useState(null);
  const latest = useRef(0);

  useEffect(() => {
    let live = true;
    getMyCredits().then((data) => { if (live) { setCredit({ state: data ? 'ok' : 'none', data }); } }).catch(() => { if (live) { setCredit({ state: 'error', data: null }); } });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    const ask = () => {
      const ticket = latest.current + 1;
      latest.current = ticket;
      getFinOps(projectId, choices || {})
        .then((data) => {
          if (!live || ticket !== latest.current) { return; }
          setAnswer({ state: data ? 'ok' : 'none', data });
          if (data && !choices) { setChoices(choicesOf(data.params)); }
        })
        .catch(() => { if (live && ticket === latest.current) { setAnswer((a) => ({ state: a.data ? 'ok' : 'error', data: a.data })); } });
    };
    const first = setTimeout(ask, choices ? DEBOUNCE_MS : 0);
    const timer = setInterval(() => { if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { ask(); } }, REFRESH_MS);
    return () => { live = false; clearTimeout(first); clearInterval(timer); };
  }, [projectId, choices]);

  const r = answer.data;
  if (!r || !choices) {
    return (
      <div className="fo-panel" data-testid="finops-panel">
        {answer.state === 'loading' && <p className="fo-muted">Working out what this project costs…</p>}
        {answer.state === 'none' && <p className="fo-empty" data-testid="finops-unavailable">FinOps needs a newer sandbox: update it from Flow&apos;s Home to see this project&apos;s costs.</p>}
        {answer.state === 'error' && <p className="fo-empty" data-testid="finops-error">The project&apos;s costs could not be worked out just now. They are asked again in a few seconds.</p>}
      </div>
    );
  }

  const symbol = r.currency?.symbol || '€';
  const runtimes = r.choices.runtimes;
  const runtimeLabel = (id) => runtimes.find((x) => x.id === id)?.label || id;
  const rates = r.flow.rates;
  const flowRows = r.flow.rows;
  const { perHour, perMonth } = r.flow;
  const estimate = r.production;
  const rows = r.scenarios;
  const scenario = r.scenario;
  const set = (patch) => setChoices((c) => ({ ...c, ...patch }));
  const tokens = credit.data?.byUnit?.tokens || null;
  const traffic = choices;

  return (
    <div className="fo-panel" data-testid="finops-panel">
      {/* The tab's title ("Project / Operate / FinOps") is the workspace's. */}
      <section aria-label="Summary" className="fo-cards">
        <div className="fo-card" data-testid="finops-card-flow">
          <span className="fo-card-cap">Running in Flow · now</span>
          <strong className="fo-card-big">{perHour === null ? '—' : `${credits(perHour)} credits/h`}</strong>
          <span className="fo-muted fo-small">
            {perHour === null
              ? (rates ? 'Nothing of this project runs now.' : 'Your plan\'s credit rates are not available.')
              : <>{r.flow.running} running · <Estimate>estimate from usage now</Estimate></>}
          </span>
        </div>
        <div className="fo-card" data-testid="finops-card-ai">
          <span className="fo-card-cap">AI for this project</span>
          <strong className="fo-card-big">Not measured yet</strong>
          <span className="fo-muted fo-small"><span className="fo-tag is-soon">AI Foundry: coming</span></span>
        </div>
        <div className="fo-card" data-testid="finops-card-prod">
          <span className="fo-card-cap">In production · per month</span>
          <strong className="fo-card-big">{money(estimate.total, { symbol })}</strong>
          <span className="fo-muted fo-small">{runtimeLabel(r.params.runtime)}, {shortCount(r.params.traffic.requestsPerDay)} requests/day · <Estimate /></span>
        </div>
        <div className="fo-card" data-testid="finops-card-per1k">
          <span className="fo-card-cap">Cost per 1,000 requests</span>
          <strong className="fo-card-big">{money(estimate.perThousand, { cents: true, symbol })}</strong>
          <span className="fo-muted fo-small">at {shortCount(r.params.traffic.requestsPerDay)}/day · <Estimate /></span>
        </div>
      </section>

      <section aria-labelledby="fo-flow-title" className="fo-section" data-testid="finops-flow">
        <div className="fo-section-head">
          <h3 id="fo-flow-title">Running in Flow</h3>
          <Estimate>credits, estimate from what runs now</Estimate>
        </div>
        {rates
          ? <p className="fo-muted fo-small" data-testid="finops-rates">Your plan&apos;s rates: {rates.cpuHour} credits per vCPU-hour · {rates.memoryGbHour} per GB-hour of memory{rates.diskGbMonth === undefined ? '' : ` · ${rates.diskGbMonth} per GB-month of disk`}.</p>
          : <p className="fo-muted fo-small" data-testid="finops-rates">Your plan&apos;s credit rates are not available here (billing is off or the sandbox is older), so only the usage is shown.</p>}
        {r.inputs?.servicesUnavailable && <p className="fo-muted fo-small">The services&apos; state could not be read just now: what runs is not counted.</p>}
        {flowRows.length === 0 && <p className="fo-empty">This project has no services yet.</p>}
        {flowRows.length > 0 && (
          <div className="fo-table-wrap">
            <DataTable
              className="fo-table"
              columns={flowColumns(r.params.hoursPerDay)}
              footer={perMonth === null ? undefined : ({ colCount, show }) => {
                const totals = ['perHour', 'perMonth'].filter(show);
                return (
                  <tr>
                    <th colSpan={colCount - totals.length} scope="row">Total if it runs like now</th>
                    {show('perHour') && <td>{credits(perHour)}</td>}
                    {show('perMonth') && <td data-testid="finops-flow-month">{credits(perMonth)}</td>}
                  </tr>
                );
              }}
              rowKey="name"
              rowProps={(row) => ({ 'data-testid': `finops-flow-${row.name}` })}
              rows={flowRows}
              storageKey="flow.finops.flow.hiddenColumns"
            />
          </div>
        )}
        <div className="fo-inline">
          <label htmlFor="fo-hours">If it runs</label>
          <input className="fo-input fo-num" id="fo-hours" max={24} min={0} onChange={(e) => set({ hoursPerDay: Math.min(24, Math.max(0, Number(e.target.value) || 0)) })} type="number" value={choices.hoursPerDay} />
          <span>hours a day</span>
        </div>
        {credit.data?.spentThisMonth && (
          <p className="fo-muted fo-small" data-testid="finops-spent">
            Your sandbox this month, all projects: {credits(credit.data.spentThisMonth.credits)} credits <Measured>metered</Measured>
          </p>
        )}
        <p className="fo-note" data-testid="finops-flow-missing">
          Not available yet: Flow meters usage per person, not per project, so this project&apos;s own usage this month is not known; disk is not split by project;
          and the price of a credit is set by your admin, so Flow figures are in credits.
        </p>
      </section>

      <section aria-labelledby="fo-ai-title" className="fo-section" data-testid="finops-ai">
        <div className="fo-section-head">
          <h3 id="fo-ai-title">AI for this project</h3>
          <span className="fo-tag is-soon">AI Foundry: coming</span>
        </div>
        <p>Flow does not measure AI cost per project yet, so there are no figures here.</p>
        <p className="fo-muted fo-small">Once AI Foundry is connected, this shows: <strong>Coding</strong> (chat, agents writing code, quality fixes) and <strong>Managing</strong> (ops agents, reviews, scheduled checks), by model with tokens in and out and their cost, per person and per run.</p>
        {tokens && (
          <p className="fo-muted fo-small" data-testid="finops-ai-tokens">
            Your AI chat this month, all projects: {credits(tokens.credits)} credits, {shortCount(tokens.tokensIn)} tokens in and {shortCount(tokens.tokensOut)} out <Measured>metered</Measured>
          </p>
        )}
      </section>

      <section aria-labelledby="fo-prod-title" className="fo-section" data-testid="finops-prod">
        <div className="fo-section-head">
          <h3 id="fo-prod-title">In production</h3>
          <Estimate />
        </div>
        <div className="fo-controls">
          <fieldset className="fo-seg-control">
            <legend className="fo-sr">Runtime</legend>
            {runtimes.map(({ id, label }) => (
              <label className={choices.runtime === id ? 'is-on' : ''} key={id}>
                <input checked={choices.runtime === id} name="fo-runtime" onChange={() => set({ runtime: id })} type="radio" value={id} />
                {label}
              </label>
            ))}
          </fieldset>
          <label className="fo-inline" htmlFor="fo-region">
            Region
            <select className="fo-input" id="fo-region" onChange={(e) => set({ region: e.target.value })} value={choices.region}>
              {r.choices.regions.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
        </div>
        <div className="fo-table-wrap">
          <DataTable
            className="fo-table"
            columns={prodColumns(symbol)}
            footer={({ colCount, show }) => (
              <tr>
                <th colSpan={show('monthly') ? colCount - 1 : colCount} scope="row">Total (estimate){show('monthly') ? '' : `: ${money(estimate.total, { symbol })}`}</th>
                {show('monthly') && <td data-testid="finops-prod-total">{money(estimate.total, { symbol })}</td>}
              </tr>
            )}
            rowKey="id"
            rows={estimate.components}
            storageKey="flow.finops.prod.hiddenColumns"
            testId="finops-prod-table"
          />
        </div>
        {r.inputs?.storesUnavailable && <p className="fo-muted fo-small">The project&apos;s datastores could not be read, so none is counted.</p>}
        <details className="fo-assumptions" open>
          <summary>Sources and assumptions</summary>
          <ul>
            {runtimes.filter((x) => x.id === r.params.runtime && x.source).map((x) => <li key={x.id}>{x.label}: {x.source}.</li>)}
            {r.assumptions.map((a) => <li key={a}>{a}</li>)}
          </ul>
          <p className="fo-muted fo-small">Deploy with Console Next to see the real bill here.</p>
        </details>
      </section>

      <section aria-labelledby="fo-whatif-title" className="fo-section" data-testid="finops-whatif">
        <div className="fo-section-head">
          <h3 id="fo-whatif-title">What if you scale</h3>
          <Estimate />
        </div>
        <div aria-label="Scenarios" className="fo-scenarios" role="group">
          {r.choices.scenarios.map((s) => (
            <button aria-pressed={scenario === s.id} className={`fo-btn${scenario === s.id ? ' is-on' : ''}`} key={s.id} onClick={() => set(s.traffic)} type="button">
              {s.label} ({shortCount(s.traffic.requestsPerDay)}/day)
            </button>
          ))}
        </div>
        <div className="fo-sliders">
          <Slider
            id="fo-requests"
            label="Requests a day"
            max={REQUEST_STEPS.length - 1}
            min={0}
            onChange={(i) => set({ requestsPerDay: REQUEST_STEPS[i] })}
            value={requestStepIndex(traffic.requestsPerDay)}
            valueText={Number(traffic.requestsPerDay).toLocaleString('en-US')}
          />
          <Slider id="fo-min" label="Autoscale: fewest replicas" max={20} min={1} onChange={(v) => set({ minReplicas: v, maxReplicas: Math.max(v, traffic.maxReplicas) })} value={traffic.minReplicas} valueText={`${traffic.minReplicas}`} />
          <Slider id="fo-max" label="Autoscale: most replicas" max={40} min={1} onChange={(v) => set({ maxReplicas: v, minReplicas: Math.min(v, traffic.minReplicas) })} value={traffic.maxReplicas} valueText={`${traffic.maxReplicas}`} />
          <Slider id="fo-busy" label="Busy hours a day" max={24} min={1} onChange={(v) => set({ busyHours: v })} value={traffic.busyHours} valueText={`${traffic.busyHours} h`} />
          <Slider
            hint="The app's own AI features, not coding."
            id="fo-ai"
            label="AI calls per request"
            max={2}
            min={0}
            onChange={(v) => set({ aiCallsPerRequest: v })}
            step={0.1}
            value={traffic.aiCallsPerRequest}
            valueText={Number(traffic.aiCallsPerRequest).toFixed(1)}
          />
          <div className="fo-slider">
            <label htmlFor="fo-ai-price">Price per AI call ({symbol})</label>
            <input className="fo-input fo-num" id="fo-ai-price" min={0} onChange={(e) => set({ aiPricePerCall: Number(e.target.value) || 0 })} step={0.001} type="number" value={traffic.aiPricePerCall} />
            <p className="fo-muted fo-small">Your model&apos;s price for one call; nothing is assumed for you.</p>
          </div>
        </div>
        <StackedChart categories={r.choices.categories} rows={rows} symbol={symbol} />
        <div className="fo-table-wrap">
          <DataTable
            className="fo-table"
            columns={scenarioColumns(symbol)}
            rowKey="id"
            rowProps={(row) => ({ 'data-testid': `finops-scenario-${row.id}` })}
            rows={rows}
            storageKey="flow.finops.scenarios.hiddenColumns"
            testId="finops-scenarios"
          />
        </div>
        <p className="fo-note">
          Every figure in this section is an estimate: list prices as of {r.pricesAsOf}, approximate, for {runtimeLabel(r.params.runtime)} in {r.params.region}.
          {r.sizing === 'default'
            ? ` Every service counts at ${r.defaultSize.vcpu} vCPU and ${r.defaultSize.memoryGb} GB.`
            : ` Services not running now count at ${r.defaultSize.vcpu} vCPU and ${r.defaultSize.memoryGb} GB.`}
        </p>
      </section>
    </div>
  );
}

FinOpsPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
};
